import { useEffect, useState } from "react";
import axios from "axios";
import { 
  Target,
  Activity,
  CheckCircle,
  Clock,
  ChevronDown,
  Award,
  Zap,
  Star,
  TrendingUp,
  TrendingDown
} from "lucide-react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const RoutineInsights = ({ userId, discipline, stats, userRoutines }) => {
    const [selectedObjective, setSelectedObjective] = useState(null);
    const [ddOption, setDdOption] = useState(userRoutines[0]?.routine?.routine_id || '');
    const [timePeriod, setTimePeriod] = useState('all');
    const [routineStats, setRoutineStats] = useState([]);
    const [selectedMetric, setSelectedMetric] = useState("attempts");
    const [isError, setIsError] = useState(false);
    const [showTable, setShowTable] = useState(false);

    useEffect(() => {
        if (selectedObjective) {
            if (timePeriod === 'all') {
                setRoutineStats([stats.find(stat => stat.id === selectedObjective.id)]);
            } else {
                axios.get(`${process.env.REACT_APP_API_HOST}/api/insights/routines/${discipline}/${userId}/${selectedObjective?.id}?period=${timePeriod}`)
                .then(response => {
                    const data = response.data;
                    if (data !== null && data.length > 0) {
                        setRoutineStats(data);
                    }
                })
                .catch(error => {
                    console.error('Error fetching data: ', error);
                    setIsError(true);
                });
            }
        }
    }, [userId, discipline, timePeriod, selectedObjective, stats])

    const handleObjectiveChange = (event) => {
        const selectedId = event.target.value;
        setDdOption(selectedId);
        const objective = stats.find(obj => obj.id === selectedId);
        setSelectedObjective(objective || null);
    };

    const metrics = [
        { id: 'attempts', label: 'Attempts', icon: Activity, color: '#3b82f6' },
        { id: 'completion_rate', label: 'Completion Rate', icon: CheckCircle, color: '#10b981' },
        { id: 'skill_completion_rate', label: 'Skill Success Rate', icon: Award, color: '#8b5cf6' },
        { id: 'average_ex', label: 'Avg Execution', icon: Star, color: '#f59e0b' },
        ...(discipline === 'trampoline' ? [
            { id: 'average_tof', label: 'Avg ToF', icon: Clock, color: '#06b6d4' },
            { id: 'average_stopwatch', label: 'Avg Stopwatch', icon: Clock, color: '#14b8a6' },
            { id: 'average_hd', label: 'Avg HD', icon: Zap, color: '#ef4444' },
        ] : []),
        ...(discipline === 'dmt' ? [
            { id: 'average_tof', label: 'Avg ToF', icon: Clock, color: '#06b6d4' },
            { id: 'average_stopwatch', label: 'Avg Stopwatch', icon: Clock, color: '#14b8a6' },
            { id: 'average_landing', label: 'Avg Landing Penalty', icon: Target, color: '#ef4444' },
        ] : []),
        ...(discipline === 'tumbling' ? [
            { id: 'average_landing', label: 'Avg Landing Penalty', icon: Target, color: '#ef4444' },
        ] : [])
    ];

    const selectedMetricData = metrics.find(m => m.id === selectedMetric);

    if (isError) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl p-6 shadow-lg text-center max-w-sm">
                    <p className="text-red-600 font-semibold">Error loading data</p>
                </div>
            </div>
        );
    }

    // const routineDates = [...new Set(routineStats?.map(routine => routine.period))];

    // Calculate summary stats
    // const totalAttempts = routineStats.reduce((sum, s) => sum + (s.attempts || 0), 0);
    // const avgCompletion = routineStats.length > 0 
    //     ? routineStats.reduce((sum, s) => sum + (s.completion_rate || 0), 0) / routineStats.length 
    //     : 0;
    // const avgSkillSuccess = routineStats.length > 0
    //     ? routineStats.reduce((sum, s) => sum + (s.skill_completion_rate || 0), 0) / routineStats.length
    //     : 0;
    // const avgExecution = routineStats.length > 0
    //     ? routineStats.reduce((sum, s) => sum + (s.average_ex || 0), 0) / routineStats.length
    //     : 0;
    // const avgTof = routineStats.length > 0 && discipline === 'trampoline'
    //     ? routineStats.reduce((sum, s) => sum + (s.average_tof || 0), 0) / routineStats.length
    //     : 0;

    // Prepare chart data
    const chartData = routineStats.map(item => ({
        period: item.period,
        attempts: item.attempts,
        completion_rate: item.completion_rate,
        skill_completion_rate: item.skill_completion_rate,
        average_ex: item.average_ex,
        average_tof: item.average_tof,
        average_stopwatch: item.average_stopwatch,
        average_hd: item.average_hd,
        average_landing: item.average_landing === 999 ? 0 : item.average_landing,
    }));

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const value = payload[0].value;
            let displayValue = value;
            
            if (selectedMetric === 'completion_rate' || selectedMetric === 'skill_completion_rate') {
                displayValue = `${value}%`;
            } else if (selectedMetric === 'average_tof' || selectedMetric === 'average_stopwatch') {
                displayValue = `${value}s`;
            }

            return (
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-900 mb-2">{label}</p>
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-xs text-gray-600">{selectedMetricData.label}:</span>
                        <span className="text-sm font-bold" style={{ color: payload[0].color }}>
                            {displayValue}
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-3 pb-6 new-entry">
            <div className="max-w-2xl mx-auto space-y-4">
                
                {/* Header */}
                <div>
                    <h2 className="text-sm text-gray-600 mt-0.5 capitalize">
                        {discipline} Routines • Progress Tracking
                    </h2>
                </div>

                {/* Routine Selector */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                    <label className="block text-base font-semibold text-gray-700 mb-2">
                        Select Routine
                    </label>
                    <div className="relative">
                        <select
                            value={ddOption || ''}
                            onChange={handleObjectiveChange}
                            className="w-full appearance-none bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 pr-10 text-gray-800 font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            style={{ fontSize: '1rem' }}
                        >
                            {userRoutines.map((obj, index) => (
                                <option key={index} value={obj.routine.routine_id} className="text-base">
                                    {obj.name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                    </div>
                </div>

                {selectedObjective ? (
                    <>
                        {/* Period Toggle */}
                        <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-200 flex gap-1">
                            {/* <button
                                onClick={() => setTimePeriod('all')}
                                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                                    timePeriod === 'all'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <span className="text-base">All Time</span>
                            </button> */}
                            <button
                                onClick={() => setTimePeriod('month')}
                                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                                    timePeriod === 'month'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <span className="text-base">Monthly</span>
                            </button>
                            <button
                                onClick={() => setTimePeriod('week')}
                                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                                    timePeriod === 'week'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <span className="text-base">Weekly</span>
                            </button>
                        </div>

                        <h3 className="text-lg font-semibold text-gray-600 mb-3">This Period</h3>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="bg-blue-100 rounded-lg p-1.5">
                                        <Activity className="text-blue-600" size={16} />
                                    </div>
                                    <p className="text-base text-gray-600">Total Attempts</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="text-2xl font-bold text-gray-900">{routineStats[0]?.attempts}</p>
                                    {routineStats?.length > 1 && (
                                        <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                                        (routineStats[0].attempts - routineStats[1].attempts) >= 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {(routineStats[0].attempts - routineStats[1].attempts) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                        {Math.abs((routineStats[0].attempts - routineStats[1].attempts)/routineStats[1].attempts * 100).toFixed(1)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            {(discipline === "trampoline") ? 
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="bg-orange-100 rounded-lg p-1.5">
                                            <Star className="text-orange-600" size={16} />
                                        </div>
                                        <p className="text-base text-gray-600">Avg ToF</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <p className="text-2xl font-bold text-gray-900">{routineStats[0]?.average_tof.toFixed(3)}s</p>
                                        {routineStats?.length > 1 && (
                                            <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                                            (routineStats[0].average_tof - routineStats[1].average_tof) >= 0 ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                            {(routineStats[0].average_tof - routineStats[1].average_tof) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                            {Math.abs((routineStats[0].average_tof - routineStats[1].average_tof)/routineStats[1].average_tof * 100).toFixed(1)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            :
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="bg-orange-100 rounded-lg p-1.5">
                                            <Star className="text-orange-600" size={16} />
                                        </div>
                                        <p className="text-base text-gray-600">Avg Execution</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <p className="text-2xl font-bold text-gray-900">{routineStats[0]?.average_ex.toFixed(1)}</p>
                                        {routineStats?.length > 1 && (
                                            <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                                            (routineStats[0].average_ex - routineStats[1].average_ex) >= 0 ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                            {(routineStats[0].average_ex - routineStats[1].average_ex) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                            {Math.abs((routineStats[0].average_ex - routineStats[1].average_ex)/routineStats[1].average_ex * 100).toFixed(1)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            }

                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="bg-green-100 rounded-lg p-1.5">
                                        <CheckCircle className="text-green-600" size={16} />
                                    </div>
                                    <p className="text-base text-gray-600">Completion Rate</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="text-2xl font-bold text-gray-900">{routineStats[0]?.completion_rate.toFixed(1)}%</p>
                                    {routineStats?.length > 1 && (
                                        <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                                        (routineStats[0].completion_rate - routineStats[1].completion_rate) >= 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {(routineStats[0].completion_rate - routineStats[1].completion_rate) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                        {Math.abs((routineStats[0].completion_rate - routineStats[1].completion_rate)/routineStats[1].completion_rate * 100).toFixed(1)}%
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="bg-purple-100 rounded-lg p-1.5">
                                        <Award className="text-purple-600" size={16} />
                                    </div>
                                    <p className="text-base text-gray-600">Skill Success Rate</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="text-2xl font-bold text-gray-900">{routineStats[0]?.skill_completion_rate.toFixed(1)}%</p>
                                    {routineStats?.length > 1 && (
                                        <span className={`text-xs font-semibold flex items-center gap-0.5 ${
                                        (routineStats[0].skill_completion_rate - routineStats[1].skill_completion_rate) >= 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {(routineStats[0].skill_completion_rate - routineStats[1].skill_completion_rate) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                        {Math.abs((routineStats[0].skill_completion_rate - routineStats[1].skill_completion_rate)/routineStats[1].skill_completion_rate * 100).toFixed(1)}%
                                        </span>
                                    )}
                                </div>
                            </div>

                        </div>

                        {routineStats.length > 1 && (
                            <>
                                {/* Metric Selector */}
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                    <label className="block text-base font-semibold text-gray-700 mb-2">
                                        Select Metric to Visualise
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={selectedMetric}
                                            onChange={(e) => setSelectedMetric(e.target.value)}
                                            className="w-full appearance-none bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 pr-10 text-gray-800 font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                                            style={{ fontSize: '1rem' }}
                                        >
                                            {metrics.map(metric => (
                                                <option key={metric.id} value={metric.id} className="text-base">
                                                    {metric.label}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                                    </div>
                                </div>

                                {/* Main Chart */}
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="rounded-lg p-1.5" style={{ backgroundColor: `${selectedMetricData.color}20` }}>
                                            <selectedMetricData.icon className="text-gray-700" size={18} style={{ color: selectedMetricData.color }} />
                                        </div>
                                        <h3 className="text-base font-bold text-gray-900">{selectedMetricData.label} Progression</h3>
                                    </div>
                                    
                                    <ResponsiveContainer width="100%" height={250}>
                                        <AreaChart data={chartData}>
                                            <defs>
                                                <linearGradient id={`color${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={selectedMetricData.color} stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor={selectedMetricData.color} stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis 
                                                dataKey="period" 
                                                tick={{ fontSize: 11 }}
                                                stroke="#9ca3af"
                                                reversed={true}
                                            />
                                            <YAxis 
                                                tick={{ fontSize: 11 }}
                                                stroke="#9ca3af"
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area 
                                                type="monotone" 
                                                dataKey={selectedMetric} 
                                                stroke={selectedMetricData.color}
                                                strokeWidth={3}
                                                fill={`url(#color${selectedMetric})`}
                                                dot={{ fill: selectedMetricData.color, r: 4 }}
                                                activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Data Table Toggle */}
                                <button
                                    onClick={() => setShowTable(!showTable)}
                                    className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                >
                                    <span className="text-sm font-semibold text-gray-900">
                                        {showTable ? 'Hide' : 'Show'} Detailed Data Table
                                    </span>
                                    <ChevronDown 
                                        size={20} 
                                        className={`text-gray-600 transition-transform ${showTable ? 'rotate-180' : ''}`}
                                    />
                                </button>

                                {/* Detailed Table */}
                                {showTable && (
                                    <div className="bg-white rounded-xl shadow-base border border-gray-200 overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-600">Period</th>
                                                        <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Attempts</th>
                                                        <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Completion</th>
                                                        <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Skill Success</th>
                                                        <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Avg Exec</th>
                                                        {discipline === 'trampoline' && (
                                                            <>
                                                                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Avg ToF</th>
                                                                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Avg SW</th>
                                                                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Avg HD</th>
                                                            </>
                                                        )}
                                                        {discipline === 'dmt' && (
                                                            <>
                                                                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Avg ToF</th>
                                                                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Avg SW</th>
                                                                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Avg Landing</th>
                                                            </>
                                                        )}
                                                        {discipline === 'tumbling' && (
                                                            <th className="px-3 py-3 text-right text-sm font-semibold text-gray-600">Avg Landing</th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {chartData.map((row, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-3 py-3 text-xs font-medium text-gray-900">{row.period}</td>
                                                            <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.attempts}</td>
                                                            <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.completion_rate}%</td>
                                                            <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.skill_completion_rate}%</td>
                                                            <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.average_ex}</td>
                                                            {discipline === 'trampoline' && (
                                                                <>
                                                                    <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.average_tof}s</td>
                                                                    <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.average_stopwatch}s</td>
                                                                    <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.average_hd}</td>
                                                                </>
                                                            )}
                                                            {discipline === 'dmt' && (
                                                                <>
                                                                    <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.average_tof}s</td>
                                                                    <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.average_stopwatch}s</td>
                                                                    <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.average_landing}</td>
                                                                </>
                                                            )}
                                                            {discipline === 'tumbling' && (
                                                                <td className="px-3 py-3 text-xs text-gray-600 text-right">{row.average_landing}</td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                        <Target className="text-gray-400 mx-auto mb-3" size={48} />
                        <p className="text-gray-600">No statistics available for the selected routine.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoutineInsights;

// import { useEffect, useState } from "react";
// import axios from "axios";
// import { FormControl, InputLabel, Select, MenuItem, ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
// import ErrorMessage from "../../../components/ErrorMessage.js";

// const RoutineInsights = ({ userId, discipline, stats, userRoutines }) => {
    
//     const [selectedObjective, setSelectedObjective] = useState(null);
//     const [ddOption, setDdOption] = useState(userRoutines[0]?.routine?.routine_id || '');
//     const [timePeriod, setTimePeriod] = useState('all');
//     const [routineStats, setRoutineStats] = useState([]);
//     const [selectedAttribute, setSelectedAttribute] = useState("attempts");
//     const [isError, setIsError] = useState(false);

//     useEffect(() => {
//         if (selectedObjective) {
//             if (timePeriod === 'all') {
//                 setRoutineStats([stats.find(stat => stat.id === selectedObjective.id)]);
//             } else {
//                 axios.get(`${process.env.REACT_APP_API_HOST}/api/insights/routines/${discipline}/${userId}/${selectedObjective?.id}?period=${timePeriod}`)
//                 .then(response => {
//                     const data = response.data;
//                     if (data !== null && data.length > 0) {
//                         setRoutineStats(data);
//                     }
//                 })
//                 .catch(error => {
//                     console.error('Error fetching data: ', error);
//                     setIsError(true);
//                 });
//             }
//         }
//     }, [userId, discipline, timePeriod, selectedObjective, stats])

//     const handleObjectiveChange = (event) => {
//         setDdOption(event.target.value)
//         const selectedId = event.target.value;
//         const objective = stats.find(obj => obj.id === selectedId);
//         setSelectedObjective(objective || null);
//     };

//     const attributes = [
//         {header: '# Attempts', field: 'attempts'},
//         {header: 'Completion Rate', field: 'completion_rate'},
//         {header: 'Skill Success Rate', field: 'skill_completion_rate'},
//         {header: 'Avg. Execution', field: 'average_ex'},
//         ...(discipline === 'trampoline' ? [
//             {header: 'Avg. ToF', field: 'average_tof'},
//             {header: 'Avg. Stopwatch', field: 'average_stopwatch'},
//             {header: 'Avg. HD', field: 'average_hd'},
//         ] : []),
//         ...(discipline === 'dmt' ? [
//             {header: 'Avg. ToF', field: 'average_tof'},
//             {header: 'Avg. Stopwatch', field: 'average_stopwatch'},
//             {header: 'Avg. Landing Pen', field: 'average_landing'},
//         ] : []),
//         ...(discipline === 'tumbling' ? [
//             {header: 'Avg. Landing Pen', field: 'average_landing'},
//         ] : [])
//     ];

//     const routineDates = [...new Set(routineStats?.map(routine => routine.period))];

//     const columns = [
//         { field: 'stat', headerName: 'Statistic', width: 120 },
//         ...routineDates?.map(date => ({ 
//             field: date, 
//             headerName: date, 
//             width: 120,
//             renderCell: params => {
//                 if (params.id === 'completion_rate') {
//                     return `${params.value || 0}%`;
//                 }
//                 else if (params.row.id === 'skill_completion_rate') {
//                     return `${params.value || 0}%`;
//                 }
//                 else if (params.row.id === 'average_landing') {
//                     return params.value === 999 ? 0 : params.value;
//                 }
//                 else if (params.row.id === 'average_tof' || params.row.id === 'average_stopwatch') {
//                     return params.value && `${params.value}s`;
//                 }
//                 return params.value;
//             }
//          }))
//     ];

//     const rows = attributes.map(attribute => ({
//         id: attribute.field,
//         stat: attribute.header,
//         ...routineDates?.reduce((acc, date) => {
//             const routine = routineStats?.find(s => s.period === date);
//             acc[date] = routine ? routine[attribute.field] : 'N/A';
//             return acc;
//         }, {}),
//     }));

//     const graphData = routineDates.map(date => ({
//         name: date,
//         value: rows.find(row => row.id === selectedAttribute)[date],
//     })).filter(attr => attr.value !== 0);

      
//     if (isError) return <ErrorMessage handleConfirm={() => setIsError(false)} />;

//     return (
//         <>
//             <form className="new-entry">
//                 <FormControl fullWidth style={{ margin: "10px 0" }}>
//                     <InputLabel id="objective-label">Routine</InputLabel>
//                     <Select
//                         labelId="objective-label"
//                         label="Routine"
//                         variant="outlined"
//                         value={ddOption || ''}
//                         onChange={handleObjectiveChange}
//                     >
//                         {userRoutines.map((obj, index) => (
//                             <MenuItem key={index} value={obj.routine.routine_id}>
//                                 {obj.name}
//                             </MenuItem>
//                         ))}
//                     </Select>
//                 </FormControl>

//                 <ToggleButtonGroup
//                     fullWidth
//                     style={{marginBottom: '10px'}}
//                     color="primary"
//                     value={timePeriod}
//                     exclusive
//                     onChange={e => setTimePeriod(e.target.value)}
//                     aria-label="By week or month"
//                 >
//                     <ToggleButton color="primary" value={'all'} aria-label="all">All Time</ToggleButton>
//                     <ToggleButton color="primary" value={'month'} aria-label="month">By Month</ToggleButton>
//                     <ToggleButton color="primary" value={'week'} aria-label="week">By Week</ToggleButton>
//                 </ToggleButtonGroup>

//                 {/* Display Stats Only If selectedObjective Exists */}
//                 {selectedObjective ? (
//                     <>                         
//                         <Box sx={{ height: '380px', width: '100%' }}>
//                             <DataGrid
//                                 rows={rows}
//                                 columns={columns}
//                                 hideFooter={true}
//                             />
//                         </Box>
//                         {routineStats.length > 1 && <>
//                             <FormControl fullWidth style={{ margin: "16px 0" }}>
//                                 <InputLabel>Graph Attribute</InputLabel>
//                                 <Select
//                                     value={selectedAttribute}
//                                     onChange={e => setSelectedAttribute(e.target.value)}
//                                 >
//                                 {rows.map(row => (
//                                     <MenuItem key={row.id} value={row.id}>
//                                         {row.stat}
//                                     </MenuItem>
//                                 ))}
//                                 </Select>
//                             </FormControl>

//                             <LineChart
//                                 width={330}
//                                 height={400}
//                                 data={graphData}
//                                 margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
//                             >
//                                 <CartesianGrid strokeDasharray="3 3" />
//                                 <XAxis 
//                                     dataKey="name"
//                                     tick={{ fontSize: 14 }}
//                                     reversed={true}
//                                 />
//                                 <YAxis 
//                                     tick={{ fontSize: 14 }}
//                                 />
//                                 <Tooltip 
//                                     contentStyle={{ fontSize: 14 }}
//                                     labelStyle={{ fontSize: 16 }}
//                                     itemStyle={{ fontSize: 16 }}
//                                 />
//                                 {/* <Legend 
//                                     wrapperStyle={{ fontSize: '16px' }}
//                                 /> */}
//                                 <Line type="monotone" dataKey="value" stroke="#8884d8" activeDot={{ r: 8 }} />
//                             </LineChart>
//                         </>}
//                     </>
//                 ) : (
//                     <p className="p-style" style={{color: 'var(--site-light-text-color)'}}>No statistics available for the selected objective.</p>
//                 )}
//             </form>
//         </>
//     );
// };

// export default RoutineInsights;
